import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { jsonapiClient } from '@/api/client'
import type { components, paths } from '@/api/schema'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler'
import { carBridge } from '@/lib/carBridge'

export type OrganizationUpdateAttributes =
  paths['/api/organizations/{id}']['patch']['requestBody']['content']['application/vnd.api+json']['data']['attributes']
import { toast } from 'vue-sonner'
import { useI18n } from 'vue-i18n'

type Organization = components['schemas']['OrganizationResource']

export const useOrganizationStore = defineStore('organization', () => {
  const { t } = useI18n()
  const { handleError } = useApiErrorHandler()

  const organizations = ref<Organization[]>([])
  const currentOrganizationId = ref<string | null>(localStorage.getItem('currentOrganizationId'))
  const loading = ref(false)

  const currentOrganization = computed(() => 
    organizations.value.find(org => org.id === currentOrganizationId.value) || null
  )

  const setOrganizations = (orgs: Organization[]) => {
    organizations.value = orgs
  }

  const setCurrentOrganization = (id: string | null) => {
    currentOrganizationId.value = id
    if (id) {
      localStorage.setItem('currentOrganizationId', id)
    } else {
      localStorage.removeItem('currentOrganizationId')
    }
    // The native car service can't read WebView localStorage — mirror the
    // active org (+ locale) into native prefs for its X-Organization-Id header.
    carBridge.setActiveContext(id)
  }

  const fetchOrganizations = async () => {
    loading.value = true
    try {
      const response = await jsonapiClient.get('/api/organizations')
      const orgs = response.data.data
      organizations.value = orgs

      // Logic for persisting/auto-selecting organization
      const storedId = localStorage.getItem('currentOrganizationId')
      const existsInList = orgs.some(org => org.id === storedId)

      if (orgs.length === 1) {
        // Auto-select if there's only one
        setCurrentOrganization(orgs[0].id)
      } else if (storedId && existsInList) {
        // Keep existing selection if it's still valid (through the setter so
        // the native car-context mirror fires on boot too).
        setCurrentOrganization(storedId)
      } else {
        // Clear if invalid or multiple exist without a selection
        setCurrentOrganization(null)
      }

      return currentOrganizationId.value
    } catch (error) {
      handleError(error)
      return null
    } finally {
      loading.value = false
    }
  }

  // The payload type is the generated PATCH schema, never a hand-written copy:
  // `attributes: data` below is a spread, invisible to TS, so a hand-written
  // type drifted both ways (locale unsendable, two fields sent that the schema
  // did not declare). A field missing from the YAML is now a compile error.
  const updateOrganization = async (id: string, data: OrganizationUpdateAttributes) => {
    try {
      const response = await jsonapiClient.patch('/api/organizations/{id}', 
        { 
          data: {
            id,
            type: 'organization',
            attributes: data
          }
        },
        { path: { id } }
      )
      const updatedOrg = response.data.data
      const index = organizations.value.findIndex(org => org.id === id)
      if (index !== -1) {
        organizations.value[index] = updatedOrg
      }
      toast.success(t('organizations.updateSuccess'))
      return updatedOrg
    } catch (error) {
      handleError(error)
      return null
    }
  }

  // ⭐ THE SHOP'S SHAPE, switch one. Off = the business names work and
  // schedules it; every soumission/évaluation surface reads this ONE getter
  // and renders nothing (nav group, intake cards, board verbs, lenses, crew
  // lanes, handheld FAB and queues). Hide, never refuse — routes stay
  // reachable by URL and the API stays open. Unknown org ⇒ on, the default.
  const salesEnabled = computed(() => currentOrganization.value?.attributes.sales_enabled ?? true)
  // Two switches for features still being shaped — the weather sensitivity and
  // the work périodes. Off (the default) hides every surface of the feature;
  // hide, never refuse. Unknown org ⇒ off, the default.
  const weatherEnabled = computed(() => currentOrganization.value?.attributes.weather_enabled ?? false)
  // Operator switch: « Migration Housecall Pro » on Importations, for the one
  // shop the importer was built for. Off (the default) hides the door and the
  // « Migrations » section — and here the API refuses too (404). Unknown org ⇒ off.
  const housecallMigrationEnabled = computed(() => currentOrganization.value?.attributes.housecall_migration_enabled ?? false)

  return {
    organizations,
    currentOrganizationId,
    currentOrganization,
    salesEnabled,
    weatherEnabled,
    housecallMigrationEnabled,
    loading,
    setOrganizations,
    setCurrentOrganization,
    fetchOrganizations,
    updateOrganization,
  }
})
